import React, { useState } from 'react';

const ComparadorRendaFixa: React.FC = () => {
  const [valorInvestido, setValorInvestido] = useState('50000');
  const [taxaLci, setTaxaLci] = useState('92');
  const [taxaCdb, setTaxaCdb] = useState('105');
  const [prazoDias, setPrazoDias] = useState('365');
  const [cdiAnual, setCdiAnual] = useState('13.25');

  const getAliquotaIR = (dias: number) => {
    if (dias <= 180) return 0.225;
    if (dias <= 360) return 0.20;
    if (dias <= 720) return 0.175;
    return 0.15;
  };

  const calcular = () => {
    const valor = parseFloat(valorInvestido) || 0;
    const lciPct = (parseFloat(taxaLci) || 0) / 100;
    const cdbPct = (parseFloat(taxaCdb) || 0) / 100;
    const dias = parseInt(prazoDias, 10) || 365;
    const cdi = (parseFloat(cdiAnual) || 0) / 100;

    if (valor <= 0 || dias <= 0) return null;

    const aliquotaIR = getAliquotaIR(dias);
    const anos = dias / 365;

    // Taxa equivalente ao CDI no período
    const rendimentoBrutoLci = valor * (Math.pow(1 + cdi * lciPct, anos) - 1);
    const liquidoLciIsenta = valor + rendimentoBrutoLci;
    const liquidoLciComIR = valor + rendimentoBrutoLci * (1 - aliquotaIR);

    const rendimentoBrutoCdb = valor * (Math.pow(1 + cdi * cdbPct, anos) - 1);
    const liquidoCdb = valor + rendimentoBrutoCdb * (1 - aliquotaIR);

    // Qual % do CDI um CDB precisa pagar para empatar com a LCI isenta?
    const cdbEquivalente = (lciPct / (1 - aliquotaIR)) * 100;

    return {
      aliquotaIR: (aliquotaIR * 100).toFixed(1),
      liquidoLciIsenta,
      lucroLciIsenta: rendimentoBrutoLci,
      liquidoLciComIR,
      lucroLciComIR: rendimentoBrutoLci * (1 - aliquotaIR),
      liquidoCdb,
      lucroCdb: rendimentoBrutoCdb * (1 - aliquotaIR),
      cdbEquivalente: cdbEquivalente.toFixed(1),
      lciVenceAtual: liquidoLciIsenta >= liquidoCdb
    };
  };

  const res = calcular();
  const formatBRL = (val: number) =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">
        Simulador de Equivalência: LCI/LCA vs. CDB (2026)
      </h3>
      <p className="text-gray-600 text-sm mb-6">
        Compare o rendimento líquido da LCI/LCA (isenta vs. cenário de tributação) com qualquer CDB e descubra qual taxa do CDI compensa mais para o seu prazo.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor a investir (R$)
              <input
                type="number"
                value={valorInvestido}
                onChange={(e) => setValorInvestido(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Taxa da LCI/LCA (% do CDI)
                <input
                  type="number"
                  value={taxaLci}
                  onChange={(e) => setTaxaLci(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                />
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Taxa do CDB (% do CDI)
                <input
                  type="number"
                  value={taxaCdb}
                  onChange={(e) => setTaxaCdb(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prazo do Resgate
                <select
                  value={prazoDias}
                  onChange={(e) => setPrazoDias(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                >
                  <option value="275">9 meses (IR CDB: 20%)</option>
                  <option value="365">1 ano (IR CDB: 17,5%)</option>
                  <option value="730">2 anos+ (IR CDB: 15%)</option>
                </select>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CDI Anual Estimado (%)
                <input
                  type="number"
                  step="0.25"
                  value={cdiAnual}
                  onChange={(e) => setCdiAnual(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Comparativo de Rentabilidade Líquida</h4>
          {res ? (
            <div className="space-y-3">
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-900">
                💡 Uma LCI/LCA de <strong>{taxaLci}% do CDI</strong> isenta equivale a um CDB pagando{' '}
                <strong>{res.cdbEquivalente}% do CDI</strong> (alíquota de IR de {res.aliquotaIR}%).
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <div>
                  <span className="font-semibold text-gray-900 block">LCI/LCA Isenta ({taxaLci}% CDI)</span>
                  <span className="text-xs text-green-600 font-medium">Lucro líquido: +{formatBRL(res.lucroLciIsenta)}</span>
                </div>
                <span className="font-bold text-green-700 text-lg">{formatBRL(res.liquidoLciIsenta)}</span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <div>
                  <span className="font-semibold text-gray-900 block">CDB ({taxaCdb}% CDI - IR {res.aliquotaIR}%)</span>
                  <span className="text-xs text-blue-600 font-medium">Lucro líquido: +{formatBRL(res.lucroCdb)}</span>
                </div>
                <span className="font-bold text-blue-700 text-lg">{formatBRL(res.liquidoCdb)}</span>
              </div>

              <div className="flex justify-between items-center py-2">
                <div>
                  <span className="font-medium text-gray-600 block text-sm">Se LCI/LCA tivesse IR ({res.aliquotaIR}%)</span>
                  <span className="text-xs text-red-600">Lucro cairia para: +{formatBRL(res.lucroLciComIR)}</span>
                </div>
                <span className="font-semibold text-gray-700">{formatBRL(res.liquidoLciComIR)}</span>
              </div>

              <div className="mt-2 pt-2 border-t border-gray-200 text-sm font-semibold text-gray-900">
                {res.lciVenceAtual
                  ? `✅ Com a regra atual de isenção, a LCI/LCA de ${taxaLci}% rende ${formatBRL(res.liquidoLciIsenta - res.liquidoCdb)} a mais que o CDB de ${taxaCdb}%.`
                  : `✅ Mesmo pagando IR, o CDB de ${taxaCdb}% supera a LCI/LCA em ${formatBRL(res.liquidoCdb - res.liquidoLciIsenta)}.`}
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Preencha os valores para simular.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparadorRendaFixa;
